import { Button, styled } from "@mui/material";
import { AxiosError } from "axios";
import { useEffect, useRef, useState } from "react";

import { useStore } from "@/app/model/RootStoreContext";
import { AmplitudeEvent } from "@/shared/config";
import { trackAmplitudeEvent } from "@/shared/lib";
import { TextareaAutosize } from "@/shared/ui";

import styles from "./styles.module.css";

const StyledTextButton = styled(Button)({
  fontFamily: "inherit",
  fontSize: "14px",
  fontWeight: "500",
  lineHeight: "18px",
  letterSpacing: "0.2px",
  textTransform: "revert",
});

interface IProps {
  dialogId?: string;
  title?: string;
  onClose: () => void;
}

const EditDialog = ({ dialogId, title = "", onClose }: IProps) => {
  const { editDialogStore, snackbarStore, dialogsStore } = useStore();

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [titleValue, setTitleValue] = useState(title);
  const [isDisabled, setIsDisabled] = useState(false);

  const handleKeyDown = (evt: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (evt.key === "Enter" && !evt.shiftKey) {
      evt.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    const { activeDialog } = dialogsStore;

    if (!dialogId) {
      return;
    }

    const formattedValue = titleValue.trim();

    if (!formattedValue.length) {
      return;
    }

    if (formattedValue === title) {
      return;
    }

    setIsDisabled(true);

    try {
      await editDialogStore.updateDialogTitle({
        dialog_id: dialogId,
        title: formattedValue,
      });

      if (activeDialog) {
        trackAmplitudeEvent(AmplitudeEvent.DialogEdited, {
          assistant_id: activeDialog.id,
          assistant_name: activeDialog.title,
          dialog_id: dialogId,
        });
      }

      onClose();
    } catch (error) {
      if (error instanceof AxiosError) {
        snackbarStore.open({
          message: error.response?.data.errorText ?? error.message,
          variant: "error",
        });
      }
    } finally {
      setIsDisabled(false);
    }
  };

  useEffect(() => {
    return () => {
      if (editDialogStore.isOpen) {
        editDialogStore.reset();
      }
    };
  }, [editDialogStore]);

  return (
    <div className={styles.wrapper}>
      <h2 className={styles.title}>Переименовать</h2>
      <TextareaAutosize
        autoFocus
        className={styles.textarea}
        ref={textareaRef}
        minRows={1}
        maxRows={10}
        maxLength={255}
        value={titleValue}
        onKeyDown={handleKeyDown}
        onChange={(evt) => setTitleValue(evt.target.value)}
      />
      <div className={styles["buttons-wrapper"]}>
        <StyledTextButton variant="text" color="custom" onClick={onClose}>
          Отменить
        </StyledTextButton>
        <StyledTextButton
          variant="text"
          loading={isDisabled}
          onClick={handleSubmit}
        >
          Сохранить
        </StyledTextButton>
      </div>
    </div>
  );
};

export default EditDialog;
